import { fieldsForType, type ImportResourceType, type ResourceColumnMapping, type ResourceField } from './types';

/**
 * Synonyms per target field, matched against normalised headers (lowercased,
 * non-alphanumerics stripped) — so "Reg Number", "reg_number" and "RegNumber"
 * all collapse to "regnumber". Order within each list is preference order.
 */
const SYNONYMS: Record<ResourceField, string[]> = {
  name: [
    'name',
    'displayvalue',
    'fleetnumber',
    'fleetno',
    'unitnumber',
    'unitno',
    'resourcename',
    'drivername',
    'vehiclename',
    'trailername',
  ],
  displayName: ['displayname', 'display', 'alias', 'shortname', 'label'],
  reference: [
    'reference',
    'regnumber',
    'registration',
    'registrationnumber',
    'regno',
    'reg',
    'numberplate',
    'plate',
    'vrn',
    'licencenumber',
    'licensenumber',
    'licence',
    'license',
    'trailernumber',
    'trucknumber',
  ],
  mobileNumber: [
    'mobilenumber',
    'mobile',
    'phonenumber',
    'phone',
    'telephone',
    'tel',
    'contactnumber',
    'msisdn',
  ],
  email: ['emailaddress', 'email', 'mail'],
  equipmentType: [
    'equipmenttype',
    'equipment',
    'trailertype',
    'vehicletype',
    'bodytype',
    'type',
    'category',
    'class',
  ],
};

/**
 * Match priority — more specific first, so a header that could satisfy several
 * targets is claimed by the strongest candidate. `name` goes last so a generic
 * header doesn't steal a column a specific field wants.
 */
const MATCH_ORDER: ResourceField[] = [
  'equipmentType',
  'mobileNumber',
  'email',
  'reference',
  'displayName',
  'name',
];

const normalise = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Match strength, strongest first. */
const PASSES: ((norm: string, syn: string) => boolean)[] = [
  (norm, syn) => norm === syn,
  (norm, syn) => norm.startsWith(syn) || norm.endsWith(syn),
  (norm, syn) => norm.includes(syn),
];

/**
 * Best-effort auto-map of source headers → target fields for the chosen
 * resource type. Only fields valid for that type are considered. Each header is
 * claimed by at most one target and each target takes at most one header.
 *
 * Passes run strength-major (every target gets its exact matches before any
 * target gets a fuzzy one), so a header like "display_value" goes to `name` —
 * which lists it exactly — instead of being grabbed by `displayName` on a
 * looser "display" prefix hit. Within a pass, {@link MATCH_ORDER} breaks ties.
 * Unmatched targets are left out (the operator maps them manually).
 */
export function autoMatchResourceColumns(
  headers: string[],
  type: ImportResourceType,
): ResourceColumnMapping {
  const allowed = new Set(fieldsForType(type).map((f) => f.id));
  const normalised = headers.map((h) => ({ raw: h, norm: normalise(h) }));
  const usedHeaders = new Set<string>();
  const mapping: ResourceColumnMapping = {};

  for (const matches of PASSES) {
    for (const target of MATCH_ORDER) {
      if (!allowed.has(target) || mapping[target]) continue;
      const syns = SYNONYMS[target];
      const hit = normalised.find(
        ({ raw, norm }) => !usedHeaders.has(raw) && syns.some((s) => matches(norm, s)),
      );
      if (hit) {
        mapping[target] = hit.raw;
        usedHeaders.add(hit.raw);
      }
    }
  }

  return mapping;
}
